import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import {
  BorrowerState,
  ENVIRONMENT,
  GetBorrowerStateResponse,
  MortgageReEntryState,
  PatchBorrowerStateRequest,
  WORKFLOW_STORAGE_KEYS,
  WorkflowContext,
  WorkflowLoanType,
} from '@ld.consumer-portal/models';
import { firstValueFrom, Observable } from 'rxjs';

import { MlaAuthService } from '../mla/mla-auth.service';
import { LoggingService } from '@ld.consumer-portal/utils';

interface RouteConfig {
  state: MortgageReEntryState;
  patterns: RegExp[];
  firstRoute: string;
}

@Injectable({
  providedIn: 'root',
})
export class WorkflowStateService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly mlaAuthService = inject(MlaAuthService);
  private readonly loggingService = inject(LoggingService);

  readonly currentState = signal<MortgageReEntryState | null>(null);

  readonly isLoading = signal(false);

  readonly error = signal<string | null>(null);

  private readonly purchaseRouteConfig: RouteConfig[] = [
    {
      state: MortgageReEntryState.Register,
      patterns: [/\/get-started$/, /\/e-consent$/],
      firstRoute: '/apply/purchase/get-started',
    },
    {
      state: MortgageReEntryState.Property,
      patterns: [
        /\/initial-questions$/,
        /\/property-information$/,
        /\/property-where$/,
        /\/under-contract$/,
        /\/additional-information$/,
        /\/sales-cost$/,
      ],
      firstRoute: '/apply/purchase/initial-questions',
    },
    {
      state: MortgageReEntryState.About,
      patterns: [
        /\/marital-status$/,
        /\/coborrower-info$/,
        /\/spouse-coborrower-info$/,
        /\/va-loan-eligibility$/,
        /\/military-service$/,
        /\/military-service-info$/,
        /\/va-loan-information$/,
        /\/current-living-situation$/,
        /\/current-housing-expenses$/,
        /\/selling-current-property$/,
        /\/address-history$/,
      ],
      firstRoute: '/apply/purchase/marital-status',
    },
    {
      state: MortgageReEntryState.Identity,
      patterns: [/\/gov-declarations$/, /\/gov-hmda$/],
      firstRoute: '/apply/purchase/gov-declarations',
    },
    {
      state: MortgageReEntryState.Income,
      patterns: [
        /\/income-source$/,
        /\/income-employed$/,
        /\/income-self-employed$/,
        /\/income-military$/,
        /\/income-pension$/,
        /\/income-social-security$/,
        /\/income-other$/,
        /\/income-summary$/,
      ],
      firstRoute: '/apply/purchase/income-source',
    },
    {
      state: MortgageReEntryState.Assets,
      patterns: [/\/known-assets$/, /\/add-assets$/, /\/verify-assets$/, /\/manual-assets$/],
      firstRoute: '/apply/purchase/known-assets',
    },
    {
      state: MortgageReEntryState.Spouse,
      patterns: [
        /\/spouse-va-eligibility$/,
        /\/spouse-military-service$/,
        /\/spouse-declarations$/,
        /\/spouse-hmda$/,
        /\/spouse-income-source$/,
      ],
      firstRoute: '/apply/purchase/spouse-va-eligibility',
    },
    {
      state: MortgageReEntryState.Submit,
      patterns: [/\/property-value$/, /\/review-application$/, /\/credit-authorization$/, /\/submission-complete$/],
      firstRoute: '/apply/purchase/property-value',
    },
  ];

  private readonly refinanceRouteConfig: RouteConfig[] = [
    {
      state: MortgageReEntryState.Register,
      patterns: [
        /\/has-loan-officer$/,
        /\/search-loan-officer$/,
        /\/confirm-loan-officer$/,
        /\/get-started$/,
        /\/e-consent$/,
      ],
      firstRoute: '/apply/refinance/has-loan-officer',
    },
    {
      state: MortgageReEntryState.Property,
      patterns: [
        /\/refinance-goals$/,
        /\/property-address$/,
        /\/additional-property-info$/,
        /\/monthly-mortgage$/,
        /\/housing-expenses$/,
      ],
      firstRoute: '/apply/refinance/refinance-goals',
    },
    {
      state: MortgageReEntryState.About,
      patterns: [
        /\/marital-status$/,
        /\/spouse-info$/,
        /\/coborrower-info$/,
        /\/va-loan-eligibility$/,
        /\/military-service$/,
        /\/military-service-info$/,
        /\/va-loan-information$/,
        /\/address-history$/,
      ],
      firstRoute: '/apply/refinance/marital-status',
    },
    {
      state: MortgageReEntryState.Income,
      patterns: [
        /\/income-source$/,
        /\/employment-history$/,
        /\/add-employment$/,
        /\/still-employed$/,
        /\/previous-employment$/,
        /\/income-summary$/,
      ],
      firstRoute: '/apply/refinance/income-source',
    },
    {
      state: MortgageReEntryState.Assets,
      patterns: [/\/add-assets$/, /\/verify-assets$/, /\/manual-assets$/],
      firstRoute: '/apply/refinance/add-assets',
    },
    {
      state: MortgageReEntryState.Identity,
      patterns: [/\/verify-credit$/, /\/credit-scores$/, /\/gov-declarations$/, /\/gov-hmda$/],
      firstRoute: '/apply/refinance/verify-credit',
    },
    {
      state: MortgageReEntryState.Submit,
      patterns: [/\/property-value$/, /\/coborrower-review$/, /\/review-application$/, /\/submission-complete$/],
      firstRoute: '/apply/refinance/property-value',
    },
  ];

  private readonly stateToRouteMap: Record<WorkflowLoanType, Partial<Record<MortgageReEntryState, string>>> = {
    purchase: {
      [MortgageReEntryState.Register]: '/apply/purchase/get-started',
      [MortgageReEntryState.Property]: '/apply/purchase/initial-questions',
      [MortgageReEntryState.About]: '/apply/purchase/marital-status',
      [MortgageReEntryState.Identity]: '/apply/purchase/gov-declarations',
      [MortgageReEntryState.Income]: '/apply/purchase/income-source',
      [MortgageReEntryState.Assets]: '/apply/purchase/known-assets',
      [MortgageReEntryState.Spouse]: '/apply/purchase/spouse-va-eligibility',
      [MortgageReEntryState.Submit]: '/apply/purchase/property-value',
    },
    refinance: {
      [MortgageReEntryState.Register]: '/apply/refinance/has-loan-officer',
      [MortgageReEntryState.Property]: '/apply/refinance/refinance-goals',
      [MortgageReEntryState.About]: '/apply/refinance/marital-status',
      [MortgageReEntryState.Income]: '/apply/refinance/income-source',
      [MortgageReEntryState.Assets]: '/apply/refinance/add-assets',
      [MortgageReEntryState.Identity]: '/apply/refinance/verify-credit',
      [MortgageReEntryState.Submit]: '/apply/refinance/property-value',
    },
  };

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  getStateFromRoute(route: string, loanType: WorkflowLoanType): MortgageReEntryState | null {
    const config = loanType === 'purchase' ? this.purchaseRouteConfig : this.refinanceRouteConfig;

    for (const routeConfig of config) {
      for (const pattern of routeConfig.patterns) {
        if (pattern.test(route)) {
          return routeConfig.state;
        }
      }
    }

    return null;
  }

  getResumeRoute(state: MortgageReEntryState, loanType: WorkflowLoanType): string | null {
    return this.stateToRouteMap[loanType][state] || null;
  }

  saveState(mortgageId: string, borrowerId: string, state: MortgageReEntryState, mlaState?: string): Observable<void> {
    const url = `${this.mlaApiUrl}api/v1/borrowerstate`;

    const body: PatchBorrowerStateRequest = {
      BorrowerID: borrowerId,
      MortgageID: mortgageId,
      WorkflowState: state,
      ...(mlaState && { MLAState: mlaState }),
    };

    return new Observable((observer) => {
      this.http.patch<void>(url, body).subscribe({
        next: () => {
          this.currentState.set(state);
          this.updateLocalContext({ lastState: state });
          observer.next();
          observer.complete();
        },
        error: (err) => {
          this.loggingService.logException(err, '[WorkflowStateService] Failed to save state');
          this.error.set('Failed to save workflow state');
          observer.error(err);
        },
      });
    });
  }

  async saveStateAsync(
    mortgageId: string,
    borrowerId: string,
    state: MortgageReEntryState,
    mlaState?: string
  ): Promise<void> {
    return firstValueFrom(this.saveState(mortgageId, borrowerId, state, mlaState));
  }

  async fetchState(mortgageId: string, loanNumber?: string): Promise<BorrowerState[]> {
    this.isLoading.set(true);
    this.error.set(null);

    const params = new URLSearchParams();
    params.append('MortgageID', mortgageId);
    if (loanNumber) {
      params.append('LoanID', loanNumber);
    }

    const url = `${this.mlaApiUrl}api/v1/borrowerstate?${params.toString()}`;

    try {
      const response = await firstValueFrom(this.http.get<GetBorrowerStateResponse>(url));
      this.isLoading.set(false);

      if (response?.BorrowerStates?.length > 0) {
        const firstState = response.BorrowerStates[0];
        if (firstState.WorkflowState !== undefined) {
          this.currentState.set(firstState.WorkflowState);
        }
        return response.BorrowerStates;
      }

      return [];
    } catch (err) {
      this.loggingService.logException(err, '[WorkflowStateService] Failed to fetch state:');
      this.error.set('Failed to fetch workflow state');
      this.isLoading.set(false);
      return [];
    }
  }

  getWorkflowContext(): WorkflowContext | null {
    const mortgageId = localStorage.getItem(WORKFLOW_STORAGE_KEYS.MORTGAGE_ID);
    const borrowerId = localStorage.getItem(WORKFLOW_STORAGE_KEYS.BORROWER_ID);
    const loanType = localStorage.getItem(WORKFLOW_STORAGE_KEYS.LOAN_TYPE) as WorkflowLoanType | null;

    if (!mortgageId || !borrowerId || !loanType) {
      return null;
    }

    const lastRoute = localStorage.getItem(WORKFLOW_STORAGE_KEYS.LAST_ROUTE) || undefined;
    const lastStateStr = localStorage.getItem(WORKFLOW_STORAGE_KEYS.LAST_STATE);
    const lastState = lastStateStr ? (parseInt(lastStateStr, 10) as MortgageReEntryState) : undefined;

    return {
      mortgageId,
      borrowerId,
      loanType,
      lastRoute,
      lastState,
    };
  }

  setWorkflowContext(context: WorkflowContext): void {
    localStorage.setItem(WORKFLOW_STORAGE_KEYS.MORTGAGE_ID, context.mortgageId);
    localStorage.setItem(WORKFLOW_STORAGE_KEYS.BORROWER_ID, context.borrowerId);
    localStorage.setItem(WORKFLOW_STORAGE_KEYS.LOAN_TYPE, context.loanType);

    if (context.lastRoute) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.LAST_ROUTE, context.lastRoute);
    }
    if (context.lastState !== undefined) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.LAST_STATE, context.lastState.toString());
    }
  }

  updateLocalContext(updates: Partial<WorkflowContext>): void {
    if (updates.mortgageId) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.MORTGAGE_ID, updates.mortgageId);
    }
    if (updates.borrowerId) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.BORROWER_ID, updates.borrowerId);
    }
    if (updates.loanType) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.LOAN_TYPE, updates.loanType);
    }
    if (updates.lastRoute) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.LAST_ROUTE, updates.lastRoute);
    }
    if (updates.lastState !== undefined) {
      localStorage.setItem(WORKFLOW_STORAGE_KEYS.LAST_STATE, updates.lastState.toString());
    }
  }

  clearWorkflowContext(): void {
    localStorage.removeItem(WORKFLOW_STORAGE_KEYS.MORTGAGE_ID);
    localStorage.removeItem(WORKFLOW_STORAGE_KEYS.BORROWER_ID);
    localStorage.removeItem(WORKFLOW_STORAGE_KEYS.LOAN_TYPE);
    localStorage.removeItem(WORKFLOW_STORAGE_KEYS.LAST_ROUTE);
    localStorage.removeItem(WORKFLOW_STORAGE_KEYS.LAST_STATE);
    this.currentState.set(null);
  }

  hasInProgressApplication(): boolean {
    return !!this.getWorkflowContext();
  }

  async getResumeRouteForInProgressApplication(): Promise<string | null> {
    const context = this.getWorkflowContext();
    if (!context) {
      return null;
    }

    if (context.lastState !== undefined && context.lastState > MortgageReEntryState.Unknown) {
      const route = this.getResumeRoute(context.lastState, context.loanType);
      if (route) {
        return route;
      }
    }

    const states = await this.fetchState(context.mortgageId);
    if (states.length > 0) {
      const borrowerState = states.find((s) => s.BorrowerID === context.borrowerId) || states[0];
      if (borrowerState.WorkflowState !== undefined && borrowerState.WorkflowState > MortgageReEntryState.Unknown) {
        return this.getResumeRoute(borrowerState.WorkflowState, context.loanType);
      }
    }

    return this.stateToRouteMap[context.loanType][MortgageReEntryState.Register] || null;
  }

  trackRouteChange(route: string, loanType: WorkflowLoanType): void {
    const state = this.getStateFromRoute(route, loanType);

    this.updateLocalContext({
      lastRoute: route,
      ...(state && { lastState: state }),
    });

    if (state) {
      this.currentState.set(state);
    }
  }
}
